import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getMinutesLeft } from '../utils/getMinutesLeft';
import { CURRENT_ROUTE_MINUTES_TICK_MS } from '../utils/constants';
import type { CurrentRouteData, UseCurrentRouteDataProps } from '../types/CleaningControls.types';
import { RobotViewModel } from 'app/modules/cleaning/widgets/robot-dashboard/RobotViewModel';
import {
  selectRoutineLastRouteName,
  selectRoutineStoppedStep,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/routineTimelineSelectors';
import type { IState } from 'app/cross-cutting-concerns/state-management/interfaces/IState';
import { isRobotCleaning } from 'app/modules/machine-inventory/utils/robotCleaningUtils';
import {
  hasRunStoppedEarly,
  isLastRouteDone,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/utils/taskStatus';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import { useRobotCleaningProgressEntry } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/hooks/useRobotCleaningProgressEntry';

/**
 * Live map current-route card: name, progress and time left from the current cleaning task (same
 * API for B50 and CV50), battery and water from telemetry.
 */
export const useCurrentRouteData = ({
  robot,
  model,
  fallbackName,
  isActive,
}: UseCurrentRouteDataProps): CurrentRouteData => {
  const entry = useRobotCleaningProgressEntry(robot, isActive);
  const lastRouteName = useSelector(selectRoutineLastRouteName);
  const stoppedStep = useSelector((state: IState) => selectRoutineStoppedStep(state, entry?.deviceTaskId));
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    setNowMs(Date.now());
    const intervalId = setInterval(() => setNowMs(Date.now()), CURRENT_ROUTE_MINUTES_TICK_MS);

    return (): void => clearInterval(intervalId);
  }, [isActive]);

  const robotModelView = useMemo(() => new RobotViewModel(robot), [robot]);
  const common = {
    name: entry?.name || fallbackName,
    isDone: isLastRouteDone(
      entry?.taskStatus,
      entry?.step,
      entry?.stepCount,
      entry?.name,
      lastRouteName,
      isRobotCleaning(robot),
      stoppedStep
    ),
    isStopped: hasRunStoppedEarly(
      entry?.taskStatus,
      entry?.step,
      entry?.stepCount,
      entry?.name,
      lastRouteName,
      isRobotCleaning(robot),
      stoppedStep
    ),
    robotStatusName: RobotUtils.getRobotStatusDisplayName(robot.robotStatus, robot.connectionStatus),
    progressPercent: entry?.progress ?? 0,
    minutesLeft: getMinutesLeft(entry?.estimatedEndTime, nowMs),
  };

  if (model === 'B50') {
    const freshWaterLevel = robotModelView.getFreshWaterLevel;

    return {
      ...common,
      batteryPercent: robotModelView.getBatteryChargeLevel,
      waterLiters: typeof freshWaterLevel === 'number' ? freshWaterLevel : undefined,
      waterPercent: robotModelView.getWaterPercentage,
    };
  }

  // Two battery packs: the lower one is shown, since it limits the remaining run time.
  return {
    ...common,
    batteryPercent: robotModelView.getBatteryChargeLevelCV50,
  };
};
